/**
 * Routes. /signin and /connect render without the shell (AuthShell); everything else needs a session and
 * renders inside the OutlierOS-replica shell (Frame). Pages not rebuilt yet show an honest placeholder.
 */
import { analyticsChoice, identifyWebAnalytics, setAnalyticsChoice, startWebAnalytics, trackPageview } from './analytics';
import { AnalyticsBanner, ConsentGate, NoticeBody, recordSignInConsent } from './components/Privacy';
import { PublicShell } from './pages/public/PublicShell';
import { useEffect, useState } from 'react';
import { BRAND } from '@vw/core/src/brand';
import { EmptyState, ErrorState, Spinner } from '@vw/ui';
import { Compass, Link2Off } from 'lucide-react';
import { api, ApiError, SESSION_CHANGED, type Me } from './api';
import { navigate, useLocation } from './router';
import { SessionProvider } from './session';
import { Frame } from './Frame';
import { forgetInvite, SignIn } from './pages/SignIn';
import { Connect } from './pages/Connect';
import { Feed } from './pages/Feed';
import { Explore } from './pages/Explore';
import { Bookmarks } from './pages/Bookmarks';
import { Creator } from './pages/Creator';
import { Collections } from './pages/Collections';
import { Collection } from './pages/Collection';
import { Welcome } from './pages/Welcome';
import { ComingSoon } from './pages/ComingSoon';
import { Compare } from './pages/Compare';
import { Ai } from './pages/Ai';
import { Billing } from './pages/Billing';
import { Earn } from './pages/Earn';
import { Settings } from './pages/Settings';
import { Suspended } from './pages/Suspended';
import { Link } from './router';
import { PublicReport } from './pages/public/PublicReport';
import { PublicCollection } from './pages/public/PublicCollection';
import { Remove } from './pages/public/Remove';
import { Waitlist } from './pages/public/Waitlist';
import { LINK_GONE_TITLE, publicRoute } from './public-route';

function page(path: string) {
  const handle = /^\/c\/([A-Za-z0-9._]{1,30})\/?$/.exec(path)?.[1];
  if (path === '/' || path === '/feed') return <Feed />;
  if (handle) return <Creator key={handle} handle={handle.toLowerCase()} />;
  if (path === '/collections') return <Collections />;
  if (/^\/collections\/[0-9a-f-]{36}$/.test(path)) return <Collection key={path} id={path.split('/')[2]} />;
  if (/^\/l\/[A-Za-z0-9_-]{22}\/?$/.test(path)) return <Collection key={path} token={path.split('/')[2]} />;
  if (path === '/welcome') return <Welcome />;
  if (path === '/explore') return <Explore />;
  if (path === '/library') return <Bookmarks />;
  if (path === '/compare') return <Compare />;
  if (path === '/settings') return <Settings />;
  if (path === '/billing') return <Billing />;
  if (path === '/earn') return <Earn />;
  if (path === '/ai') return <Ai />;
  return <EmptyState icon={<Compass className="h-7 w-7" />} title="Page not found" actions={<Link to="/feed" className="text-sm font-semibold text-primary">Back to Your Feed →</Link>}>Check the address, or use Search (⌘K).</EmptyState>;
}

export function App() {
  const { path } = useLocation();
  const [me, setMe] = useState<Me | null | 'loading' | 'banned'>('loading');
  const [attempt, setAttempt] = useState(0);
  const bare = path === '/signin' || path === '/connect';
  useEffect(() => trackPageview(), [path]);
  // Web analytics (decision 96): loaded only with consent; the website's cookies are deleted only after a "No".
  useEffect(() => startWebAnalytics(), []);
  // Usage statistics are asked once (opt-in); a signed-in user's saved choice is reused in this browser.
  const [askStats, setAskStats] = useState(() => analyticsChoice() === null);
  // Signed in: the account's choice wins in every browser; a choice made here before it existed is sent up once.
  useEffect(() => {
    if (!me || typeof me !== 'object' || !me.privacy || me.privacy.needsConsent) return;
    const local = analyticsChoice();
    if (me.privacy.analytics !== null && local !== me.privacy.analytics) { setAnalyticsChoice(me.privacy.analytics); setAskStats(false); }
    else if (me.privacy.analytics === null && local !== null) void api('/api/privacy/consent', { method: 'POST', json: { purpose: 'analytics', granted: local, source: 'banner' } }).catch(() => undefined);
  }, [me]);
  useEffect(() => {
    if (me && typeof me === 'object' && analyticsChoice() === true) identifyWebAnalytics(me.user);
  }, [me, askStats]); // askStats: also right after a "Yes" on the statistics banner
  // Agreement ticked on the sign-in page (this tab): record it once, right after sign-in.
  useEffect(() => {
    if (me && typeof me === 'object' && me.privacy?.needsConsent && !me.privacy.withdrawnAt) {
      void recordSignInConsent().then((p) => { if (p) setMe({ ...me, privacy: p }); });
    }
  }, [me]);
  // Any call that finds the session gone, banned or awaiting consent re-checks /api/me (once at a time).
  const signedIn = !!me && typeof me === 'object';
  useEffect(() => {
    if (!signedIn) return;
    const recheck = () => setAttempt((a) => a + 1);
    window.addEventListener(SESSION_CHANGED, recheck);
    return () => window.removeEventListener(SESSION_CHANGED, recheck);
  }, [signedIn]);
  const statsBanner = askStats && path !== '/privacy-notice' ? <AnalyticsBanner signedIn={!!me && typeof me === 'object'} onDone={() => setAskStats(false)} /> : null;

  useEffect(() => {
    if (path === '/signin' || path === '/remove' || path === '/waitlist' || path === '/privacy-notice') return;
    const here = location.pathname + location.search;
    let live = true;
    api<Me>('/api/me').then((m) => {
      if (!live) return;
      setMe(m);
      forgetInvite(); // signed in: a beta invite code remembered by /signin has done its job (decision 106)
      // A brand-new account (nothing done yet) opening the app lands on getting started (OutlierOS /welcome).
      if (location.pathname === '/') {
        api<{ done: number }>('/api/onboarding').then((o) => {
          if (o.done === 0 && location.pathname === '/') navigate('/welcome', { replace: true });
        }, () => undefined);
      }
    }, (err) => {
      if (!live) return;
      if (err instanceof ApiError && err.code === 'banned') return setMe('banned');
      setMe(null);
      const isPublic = /^\/(r|l)\/|^\/remove|^\/waitlist|^\/privacy-notice/.test(location.pathname);
      if (err instanceof ApiError && err.status === 401 && location.pathname !== '/signin' && !isPublic) {
        navigate(`/signin?return_to=${encodeURIComponent(here)}`, { replace: true });
      }
    });
    return () => { live = false; };
  }, [path === '/signin' || path === '/remove' || path === '/waitlist', attempt]);

  if (path === '/privacy-notice') return <PublicShell cta={null}><h1 className="text-3xl font-extrabold">Privacy notice</h1><NoticeBody /></PublicShell>;
  if (path === '/signin') return <>{statsBanner}<SignIn /></>;
  // Public pages: no sign-in needed (signed-in viewers get the in-app view of a collection link).
  if (path === '/remove') return <>{statsBanner}<Remove /></>;
  if (path === '/waitlist') return <>{statsBanner}<Waitlist /></>;
  // Any /r/* or /l/* is a public page; a malformed token can't be valid, so it says so without asking the API.
  const pub = publicRoute(path);
  if (pub && !pub.token) return <>{statsBanner}<PublicShell cta={null}><EmptyState icon={<Link2Off className="h-7 w-7" />} title={LINK_GONE_TITLE}>Check you copied the whole link, or ask the person who shared it for a new one.</EmptyState></PublicShell></>;
  if (pub && me === 'loading') return <Spinner className="min-h-screen" />;
  if (pub?.kind === 'r') return <>{statsBanner}<PublicReport token={pub.token!} signedIn={!!me && typeof me === 'object'} /></>;
  if (pub?.kind === 'l' && (me === null || me === 'banned')) return <>{statsBanner}<PublicCollection token={pub.token!} /></>;
  if (me === 'loading') return <Spinner className="min-h-screen" label={`Signing you in to ${BRAND.name}…`} />;
  if (me === 'banned') return <Suspended />;
  if (!me) return <div className="flex min-h-screen items-center p-6"><ErrorState message={`Couldn't reach ${BRAND.name}. Check your connection and try again.`} onRetry={() => { setMe('loading'); setAttempt((a) => a + 1); }} /></div>;
  // DPDP (decision 44): agree to the current privacy notice before anything else (the API refuses until then).
  if (me.privacy?.needsConsent) return <ConsentGate email={me.user.email} privacy={me.privacy} onAgreed={(p) => setMe({ ...me, privacy: p })} />;
  if (bare) return <Connect />;
  return (
    <SessionProvider me={me}>
      <Frame path={path}>{page(path)}</Frame>
      {statsBanner}
    </SessionProvider>
  );
}
